import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);
const STORAGE_BUCKET = 'product-images';

export async function listProductosRpc({ search = '', categoria = '', provincia = '', idTipoEntrega = '' } = {}) {
	const params = {
		_categoria: categoria || null,
		_idtipoentrega: idTipoEntrega ? Number(idTipoEntrega) : null,
		_provincia: provincia || null,
		_search: search || null,
	};

		const started = Date.now();
	try {
		// Logs de depuración, recordar borrar o comentar después
		const { data, error } = await supabase.rpc('listproductos', params);
		const ms = Date.now() - started;
			console.log('[listProductosRpc] params:', params, 'ms:', ms);
		if (error) {
				console.error('[listProductosRpc] error:', error);
			return { data: [], error };
		}
			console.log('[listProductosRpc] rows:', data?.length ?? 0, 'sample:', data?.[0]);
		return { data: data || [], error: null };
	} catch (err) {
		console.error('[listProductosRpc] exception:', err);
		return { data: [], error: err };
	}
}

// Detalle de producto por ID: intenta SELECT directo y si falla, intenta RPC 'getproducto'
export async function getProductoById(id) {
	if (!id && id !== 0) return { data: null, error: new Error('id requerido') };
	try {
		const started = Date.now();
		const { data, error } = await supabase
			.from('Productos')
			.select('*')
			.eq('id', Number(id))
			.single();
		// Logs de depuración, recordar borrar o comentar después
		const ms = Date.now() - started;
		console.log('[getProductoById] select Productos id=', id, 'ms:', ms, 'error?', !!error);
		if (!error) return { data, error: null };
		console.warn('[getProductoById] SELECT falló, intentando RPC getproducto', error);
	} catch (e) {
		console.warn('[getProductoById] excepción en SELECT, intentando RPC', e);
	}
}

export async function updateProductoEstado(id, idEstado) {
	try {
		console.log('[updateProductoEstado] try direct UPDATE', { id: Number(id), idEstado: Number(idEstado) });
		const upd = await supabase
			.from('Productos')
			.update({ idEstado: Number(idEstado) })
			.eq('id', Number(id))
			.select('id, idEstado');

		if (upd.error) return { data: null, error: upd.error };
		const rows = Array.isArray(upd.data) ? upd.data : [];
		console.log('[updateProductoEstado] direct UPDATE affected rows:', rows.length, rows[0]);
		if (rows.length > 0) return { data: rows[0], error: null };

		// Fallback a RPC si no se pudo devolver la representación (p. ej. RLS o policies)
		try {
			console.log('[updateProductoEstado] fallback RPC updateproductoestado');
			const { data, error } = await supabase.rpc('updateproductoestado', {
				_id: Number(id),
				_estado: Number(idEstado),
			});
			if (error) return { data: null, error };
			const row = Array.isArray(data) ? data[0] : data;
			console.log('[updateProductoEstado] RPC updateProductoEstado data:', row);
			if (row != null) return { data: row, error: null };

			// Segundo intento: nombre en minúsculas
			console.log('[updateProductoEstado] fallback RPC updateproductoestado (lowercase)');
			const { data: data2, error: error2 } = await supabase.rpc('updateproductoestado', {
				_id: Number(id),
				_estado: Number(idEstado),
			});
			if (error2) return { data: null, error: error2 };
			const row2 = Array.isArray(data2) ? data2[0] : data2;
			console.log('[updateProductoEstado] RPC updateproductoestado data:', row2);
			return { data: row || null, error: null };
		} catch (e) {
			console.error('[updateProductoEstado] excepción en RPC fallback:', e);
			return { data: null, error: e };
		}
	} catch (err) {
		console.error('[updateProductoEstado] excepción:', err);
		return { data: null, error: err };
	}
}

// Imagenes: helpers de Storage
export function getImagePublicUrl(path) {
	if (!path) return null;
	try {
		const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
		return data?.publicUrl || null;
	} catch (e) {
		console.warn('[getImagePublicUrl] error:', e);
		return null;
	}
}

export async function uploadProductoImage(file, { productId, ownerNumDocumento }) {
	if (!file) return { path: null, publicUrl: null, error: new Error('file requerido') };
	try {
		const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase();
		const safeOwner = ownerNumDocumento ? String(ownerNumDocumento) : 'unknown';
		const folder = productId ? `products/${productId}` : `users/${safeOwner}/products`;
		const fileName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
		const key = `${folder}/${fileName}`;

		const { data, error } = await supabase.storage
			.from(STORAGE_BUCKET)
			.upload(key, file, { cacheControl: '3600', upsert: false, contentType: file.type || undefined });
		if (error) return { path: null, publicUrl: null, error };
		const publicUrl = getImagePublicUrl(data.path);
		return { path: data.path, publicUrl, error: null };
	} catch (err) {
		console.error('[uploadProductoImage] excepción:', err);
		return { path: null, publicUrl: null, error: err };
	}
}

// Firmar URL para buckets privados (sirve también para públicos)
export async function getImageSignedUrl(path, expiresInSeconds = 60 * 60) {
	if (!path) return { url: null, error: new Error('path requerido') };
	try {
		const { data, error } = await supabase.storage
			.from(STORAGE_BUCKET)
			.createSignedUrl(path, expiresInSeconds);
		if (error) return { url: null, error };
		return { url: data?.signedUrl || null, error: null };
	} catch (err) {
		console.error('[getImageSignedUrl] excepción:', err);
		return { url: null, error: err };
	}
}

// Insertar un producto en la tabla Productos y devolver el id
export async function insertProducto(row) {
	try {
		const payload = { ...row };
		// Limpieza de tipos
		if (payload.precio != null) payload.precio = Number(payload.precio);
		if (payload.idTipoEntrega != null) payload.idTipoEntrega = Number(payload.idTipoEntrega);
		if (payload.idEstado != null) payload.idEstado = Number(payload.idEstado);

		const { data, error } = await supabase
			.from('Productos')
			.insert(payload)
			.select('id')
			.single();
		if (error) return { id: null, error };
		return { id: data?.id ?? null, error: null };
	} catch (err) {
		console.error('[insertProducto] excepción:', err);
		return { id: null, error: err };
	}

}

export async function listResennas(){
    try{
        const {data, error} = await supabase.rpc('getallresennas')
        return data;
    }catch (err){
        console.error('[listResennas] exception:', err)
        return { data: null, error: err };
    }
}

export async function updateStateResenna(id){
    try{
        const {data, error} = await supabase.rpc('updateresenna', {_id: id})
        return data;
    }catch (err){
        console.error('[updateStateResenna] exception:', err)
        return { data: null, error: err };
    }
}